import { getPawaPayPayment } from "api/invoiceApiCall";
import React, { useEffect, useState } from "react";
import { usePaystackPayment } from "react-paystack";
import { useParams } from "react-router-dom";
import { Button, Card, CardHeader, Modal, ModalHeader, ModalFooter, ModalBody } from "reactstrap";
import { useTranslation } from '../../../i18n/context';

import { convertCurrency } from "api/invoiceApiCall";
import { getPaymentInvoice } from "api/invoiceApiCall";


const PaymentConfirmationModal = ({ 
  invoice, 
  isOpen, 
  handleClose,
  onPaymentSuccess
}) => {
  const { token } = useParams();
  const { t } = useTranslation();

  
  const [formModal, setFormModal] = useState(false);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paystackConfig, setPaystackConfig] = useState(null);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);

  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  // Currency conversion for Nigerian customers
  const initializePaystackConfig = async () => {
    let amount = invoice.subtotal;

    if (invoice?.customer_country?.toLowerCase() === 'nigeria') {
      try {
        setIsLoading(true);
        const convertedData = await convertCurrency(amount, 'NGN', 'GHS');
        amount = convertedData.convertedAmount || amount;
        setConvertedAmount(amount);
      } catch (error) {
        console.error('Failed to convert amount:', error);
      } finally {
        setIsLoading(false);
      }
    }

    const config = {
      reference: invoice._id,
      email: invoice.customer_email,
      amount: Math.round(amount * 100),
      currency: "GHS", // Always use GHS
      publicKey: publicKey,
    };

    setPaystackConfig(config);
  };

  // Paystack payment initialization
  const initializePayment = usePaystackPayment(paystackConfig || {
    reference: invoice._id,
    email: invoice.customer_email,
    amount: invoice.subtotal * 100,
    currency: "GHS",
    publicKey: publicKey,
  });

  // Modal and payment setup
  useEffect(() => {
    if (invoice && invoice?._id && isOpen) {
      setFormModal(true);
      // If Nigerian customer, get conversion rate when modal opens
      if (invoice?.customer_country?.toLowerCase() === 'nigeria') {
        initializePaystackConfig();
      }
    } else {
      setFormModal(false);
    }
  }, [invoice, isOpen]);

  // Handle successful payment
  const handlePaystackSuccessAction = async (reference) => {
    try {
      // Verify payment with your backend
      const [error, paymentVerification] = await getPaymentInvoice(reference);
      
      if (!error && paymentVerification) {
        // Update payment status
        setPaymentSuccessful(true);
        
        // Call the onPaymentSuccess callback if provided
        if (onPaymentSuccess) {
          onPaymentSuccess(paymentVerification);
        }
      } else {
        // Handle payment verification failure
        console.error("Payment verification failed");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
    }
  };

  // Handle payment modal close
  const handlePaystackCloseAction = () => {
    handleClose();
  };

  // Initiate Paystack payment
  const initiatePaystackPayment = () => {
    initializePayment(handlePaystackSuccessAction, handlePaystackCloseAction);
    setFormModal(!formModal);
    handleClose();

  };

  // Alternative payment method for other countries
  const PawaPayInitiator = async () => {
    try {
      const invoiceData = await getPawaPayPayment(invoice?._id);
      if (
        invoiceData?.status === 200 &&
        invoiceData?.data?.paymentLink?.redirectUrl
      ) {
        // Redirect to payment link
        window.location.href = invoiceData?.data?.paymentLink?.redirectUrl;
      }
    } catch (error) {
      console.error("PawaPay initiation failed:", error);
    }
  };

  // Display amount with currency conversion
  const displayAmount = () => {
    const amount = invoice.subtotal;
    const country = invoice?.customer_country?.toLowerCase();

    if (country === 'nigeria') {
      if (isLoading) return "Calculating amount...";
      if (convertedAmount) {
        return (
          <>
            <div>({t('invoice.amount')}): NGN {amount.toFixed(2)}</div>
            <div className="mt-1">({t('invoice.equivalent')}): GHS {convertedAmount.toFixed(2)}</div>
            <small className="text-muted mt-1">
              ({t('invoice.proccessed')})
            </small>
          </>
        );
      }
      return `Amount: NGN ${amount.toFixed(2)}`;
    } else if (country === 'ghana') {
      return `Amount: GHS ${amount.toFixed(2)}`;
    } else {
      return `Amount: XOF ${amount.toFixed(2)}`;
    }
  };

  // Close success modal
  const handleCloseSuccessModal = () => {
    setPaymentSuccessful(false);
    handleClose();
  };

  return (
    <>
      <Modal
        className="modal-dialog-centered"
        size="sm"
        isOpen={formModal}
        toggle={() => handleClose()}
      >
        <div className="modal-body p-0">
          <Card className="bg-secondary border-0 mb-0">
            <CardHeader className="bg-transparent pb-5">
              <div className="text-muted text-center mt-2 mb-3">
                <small>{t('invoice.payment_confrim')}</small>
                <h5 style={{ marginTop: "20px" }}>{t('invoice.proceed')}</h5>
                <div className="mt-2">{displayAmount()}</div>
              </div>
              {["ghana", "nigeria"].includes(
                invoice?.customer_country?.toLowerCase()
              ) ? (
                <Button
                  block
                  color="default"
                  onClick={initiatePaystackPayment}
                  disabled={isLoading}
                >
                  {isLoading ? "Calculating..." : t('invoice.pay_now_stack')}
                </Button>
              ) : (
                <Button
                  block
                  color="default"
                  onClick={PawaPayInitiator}
                  // disabled={setPaymentSuccessful}
                >
                  {t('invoice.pay_now')}
                </Button>
              )}
            </CardHeader>
          </Card>
        </div>
      </Modal>

      {/* Payment Success Modal */}
      <Modal isOpen={paymentSuccessful} toggle={handleCloseSuccessModal}>
        <ModalHeader toggle={handleCloseSuccessModal}>
          {t('invoice.payment_success')}
        </ModalHeader>
        <ModalBody>
          {t('invoice.payment_success_message')}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCloseSuccessModal}>
            {t('common.close')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PaymentConfirmationModal;